import {
  MESSENGER_V2_COMPOSER_ACTIVITY_RECORDING_VOICE,
  MESSENGER_V2_COMPOSER_ACTIVITY_TYPING,
  normalizeMessengerV2ComposerActivity,
} from './composerActivity.js';

const TYPING_IDLE_MS = 2500;
const ACTIVITY_KEEPALIVE_MS = 1200;

const createIdleActivity = () => ({
  userId: null,
  conversationId: null,
  activity: null,
  lastSentAt: 0,
});

export const createMessengerV2ComposerActivityPublisher = ({
  sendActivity,
  now = () => Date.now(),
  setTimer = (callback, delay) => window.setTimeout(callback, delay),
  clearTimer = (timerId) => window.clearTimeout(timerId),
} = {}) => {
  let current = createIdleActivity();
  let timerId = null;

  const cancelScheduledActivity = () => {
    if (timerId == null) return;
    clearTimer(timerId);
    timerId = null;
  };

  const transmit = (state, activity) => {
    if (!state.userId || !state.conversationId || typeof sendActivity !== 'function') return;
    sendActivity(state.userId, state.conversationId, Boolean(activity), activity);
  };

  const scheduleActivityLifecycle = () => {
    cancelScheduledActivity();
    const expected = { ...current };
    const delay =
      expected.activity === MESSENGER_V2_COMPOSER_ACTIVITY_TYPING
        ? TYPING_IDLE_MS
        : ACTIVITY_KEEPALIVE_MS;

    timerId = setTimer(() => {
      timerId = null;
      const activityUnchanged =
        current.userId === expected.userId &&
        current.conversationId === expected.conversationId &&
        current.activity === expected.activity;
      if (!activityUnchanged) return;

      if (current.activity === MESSENGER_V2_COMPOSER_ACTIVITY_RECORDING_VOICE) {
        transmit(current, current.activity);
        current = { ...current, lastSentAt: now() };
        scheduleActivityLifecycle();
        return;
      }

      transmit(current, null);
      current = { ...current, activity: null, lastSentAt: 0 };
    }, delay);
  };

  const reset = ({ notify = false } = {}) => {
    cancelScheduledActivity();
    if (notify && current.activity) transmit(current, null);
    current = createIdleActivity();
  };

  const publish = ({ userId = null, conversationId = null, activity = null } = {}) => {
    const next = {
      userId: String(userId ?? '').trim() || null,
      conversationId: String(conversationId ?? '').trim() || null,
      activity: normalizeMessengerV2ComposerActivity(activity),
    };

    const targetChanged =
      current.userId !== next.userId || current.conversationId !== next.conversationId;
    if (targetChanged && current.activity) transmit(current, null);

    if (!next.userId || !next.conversationId) {
      reset();
      return;
    }

    if (!next.activity) {
      if (!targetChanged && current.activity) transmit(current, null);
      cancelScheduledActivity();
      current = { ...next, lastSentAt: 0 };
      return;
    }

    const sentAt = now();
    const activityChanged = targetChanged || current.activity !== next.activity;
    const keepaliveDue = sentAt - Number(current.lastSentAt || 0) >= ACTIVITY_KEEPALIVE_MS;
    if (activityChanged || keepaliveDue) {
      transmit(next, next.activity);
      current = { ...next, lastSentAt: sentAt };
    } else {
      current = { ...next, lastSentAt: current.lastSentAt };
    }
    scheduleActivityLifecycle();
  };

  return Object.freeze({ publish, reset });
};
